'use strict'

import { filterData } from './helpers.js'
import { axiosGet, axiosPost } from '../axios/axios.js'

export async function filterFn (searchValue, update, abort, config) {
  if (config.loading) {
    // Когда данные еще не подгрузились
    config.updateFilterSearchValue = searchValue
    config.updateFilterFn = (callback) => {
      update(callback)
    }
    return
  }

  await config.load(searchValue)

  if (config.updateFilterFn) {
    searchValue = config.updateFilterSearchValue
    config.updateFilterSearchValue = null
    update = config.updateFilterFn
    config.updateFilterFn = null
  }

  update(() => {
    config.optionsFiltered = config.store.filterAfterLoad(searchValue) // TODO
  })
}

export function createFieldConfig () {
  let store
  if (arguments[0] instanceof Store) {
    store = arguments[0]
  } else if (typeof arguments[0] === 'function') {
    store = new Store({
      itemsFn: arguments[0],
      cached: true,
      searchFiltrationBySource: !!arguments[1],
      updateInterval: 30000, // По историческим причинам
      searchColumns: arguments[2],
      searchable: true,
      fullReadable: !arguments[1]
    })
  } else {
    const config = arguments[0]
    store = new Store({
      itemsFn: config.allRecordsFn,
      cached: config.cached && config.storeType === 'server', // server, local
      searchFiltrationBySource: false,
      searchColumns: config.searchColumns,
      searchable: true,
      fullReadable: true
    })
  }

  const fieldConfig = {
    focus: false,
    optionsFiltered: [],
    updateFilterFn: null,
    updateFilterSearchValue: null,

    store: store,
    get loading () {
      return this.store.loading
    },
    load (val) {
      return this.store.load(val)
    },
    reload () {
      return this.store.reload()
    },
    reset () {
      this.store.reset()
      this.optionsFiltered = []
    }
  }
  fieldConfig.filterFn = function (val, update, abort) {
    return filterFn(val, update, abort, fieldConfig)
  }
  return fieldConfig
}

export class Store {
  /**
   items
   itemsFn
   proxy
   cached
   updateInterval

   fullReadable
   searchable
   searchFiltrationBySource
   searchValueMinLength
   searchColumns
   alwaysShowUnselected (только для случаев, когда items получены локально: !proxy)
  */

  constructor (config) {
    this.loadTime = 0
    this.loading = false
    this._items = []
    this._itemsFiltered = []
    this.itemsFn = null
    this.itemsTotal = null
    this.searchColumns = config.searchColumns || ['name']
    this.alwaysShowUnselected = config.alwaysShowUnselected

    if (config.items) {
      this.items = config.items
      this.cached = false // direct access
      this.updateInterval = 0

      this.fullReadable = true
      this.searchable = config.searchable || true
      if (this.searchable) {
        this.searchFiltrationBySource = false
      }
    } else {
      this.fullReadable = config.fullReadable || false

      const proxy = config.proxy || null
      this.proxy = proxy

      if (config.itemsFn) {
        this.itemsFn = this.getWrappedItemsFn(config.itemsFn)
      }

      if (config.proxy) {
        const getItemsFn = function (selectAll = false) {
          return function (searchValue, optionValue, optionLabel) {
            proxy.searchValue = searchValue || null

            const urlParams = new URLSearchParams()

            if (proxy.formId) {
              urlParams.append('formId', proxy.formId)
            }
            if (proxy.urlParams) {
              Object.entries(proxy.urlParams).forEach(([key, value]) => urlParams.append(key, value))
            }

            if (proxy.searchParamKey && !proxy.searchCustom) {
              if (proxy.searchValue) {
                urlParams.append(proxy.searchParamKey, proxy.searchValue)
              }
              if (proxy.urlBase && proxy.urlBase.startsWith('/service/delta/')) {
                urlParams.set('filter', 'none')
              }
            }

            let bodyFormData = null
            if (proxy.post && proxy.bodyParams) {
              bodyFormData = new FormData()
              for (const [key, value] of Object.entries(proxy.bodyParams)) {
                bodyFormData.set(key, value)
              }
            }
            if (!selectAll) {
              urlParams.append('start', 0)
              urlParams.append('limit', 20)
            }

            if (selectAll) {
              if (proxy.urlBase === '/service/obj/search') {
                proxy.selectAll.urlParams = {
                  returnFields: optionValue && optionLabel ? [optionValue, optionLabel].toString() : 'id,name',
                  limit: 10000000
                }
              }
              if (proxy.selectAll.urlParams) {
                Object.entries(proxy.selectAll.urlParams).forEach(([key, value]) => urlParams.append(key, value))
              }
            }

            const url = proxy.urlBase + //
              (proxy.urlBase.includes('?') ? '&' : '?') + //
              urlParams.toString()

            const requestFn = proxy.post ? axiosPost : axiosGet
            return requestFn({
              url: url,
              data: bodyFormData,
              onSuccess: (response) => {
                if (!selectAll) {
                  this.itemsTotal = response.data.total != null ? response.data.total : response.data.count != null ? response.data.count : response.data.result.length
                }

                let items = response.data.result

                const mapper = selectAll ? proxy.selectAll.mapper : proxy.mapper
                if (mapper) {
                  items = items.map(mapper)
                }
                return items
              },
              onFailure: 'Ошибка при загрузке данных',
              throws: true
            })
          }
        }

        if (!this.itemsFn) {
          this.itemsFn = getItemsFn()
        }

        if (!this.fullReadable && (proxy.selectAll != null || proxy.urlBase === '/service/obj/search')) {
          if (!proxy.selectAll) {
            proxy.selectAll = {}
          }
          proxy.selectAll.itemsFn = getItemsFn(true)
        }
      }

      if (config.cached != null) {
        this.cached = config.cached
        this.updateInterval = config.updateInterval || 24 * 3600000
      } else if (config.updateInterval) {
        this.cached = true
        this.updateInterval = config.updateInterval
      } else {
        this.cached = false
        this.updateInterval = 0
      }

      this.searchable = config.searchable || false
      if (this.searchable) {
        this.searchFiltrationBySource = this.fullReadable ? !!config.searchFiltrationBySource : true
        if (this.searchFiltrationBySource) {
          if (config.searchValueMinLength != null) {
            this.searchValueMinLength = config.searchValueMinLength
          } else {
            this.searchValueMinLength = this.fullReadable ? 0 : 3
          }
        } else {
          this.searchValueMinLength = 0
        }
      }
    }
  }

  getWrappedItemsFn (itemsFnRaw, items) {
    if (itemsFnRaw) {
      return function (val) {
        const result = itemsFnRaw(val)
        if (result instanceof Promise) {
          return result
        }
        return Promise.resolve(result)
      }
    } /* else {
      if (items) {
        return function () {
          return Promise.resolve(items)
        }
      } else {
        // TODO !!!
      }
    } */
    return null
  }

  isExpired (currentTime) {
    if (this.cached) {
      if (currentTime) {
        currentTime = new Date().getTime()
      }
      return this.loadTime < currentTime - this.updateInterval
    }
    return true
  }

  load (searchValue) {
    let promise = null
    if (this.searchFiltrationBySource) {
      // Серверная фильтрация. Количество введенных символов не менее searchValueMinLength
      if (searchValue ? searchValue.length < this.searchValueMinLength : this.searchValueMinLength > 0) {
        // Возвращаем пустой список
        if (!this._items || this._items.length) {
          this._items = []
          this._itemsFiltered = []
        }
        return Promise.resolve(this._items)
      }
      this.loading = true
      promise = this.loadFromSource(searchValue)
    } else {
      // Клиентская фильтрация
      this.loading = true
      const currentTime = new Date().getTime()
      if (this.isExpired(currentTime)) {
        promise = this.loadFromSource('', currentTime) // К серверу пойдет пустой паттерн, чтобы он ничего не фильтровал
      } else {
        promise = Promise.resolve(this._items || [])
      }
    }

    return promise
      .then(items => {
        this._itemsFiltered = this.filterAfterLoad(searchValue, items)
        return this._itemsFiltered
      })
      .catch((e) => {
        this._itemsFiltered = []
        return Promise.reject(e)
      })
      .finally(() => {
        this.loading = false
      })
  }

  loadFromSource (searchValue, currentTime) {
    this.loadTime = currentTime
    // this.loading = true
    return this.itemsFn(searchValue) // TODO
      .then(items => {
        this._items = items || []
        return this._items
      })
      .catch((e) => {
        this._items = []
        return Promise.reject(e)
      })
      // .finally(() => { this.loading = false })
  }

  filterAfterLoad (val, items = this._items) {
    if (this.searchFiltrationBySource || !val) {
      return items
    } else {
      return filterData(items, val, this.searchColumns)
    }
  }

  reload () {
    this.reset()
    return this.load()
  }

  reset () {
    this.loadTime = 0
    this._items = []
    this._itemsFiltered = []
    // if (this.proxy && this.proxy.searchValue) {
    //   this.proxy.searchValue = null
    // }
  }
}
